import { error, EventTarget } from 'cc';

enum EventType {
    VALUE_CHANGED = 'VALUE_CHANGED',
    OPTIONS_UPDATED = 'OPTIONS_UPDATED',
}

export interface IValueSet<T> {
    defaultIndex?: number;
    values: Readonly<T[]>;
}

export default class ValueSelector<T> extends EventTarget {
    // not using -1 to avoid conflict with cycleSelectionFromTail calculation
    public static readonly NULL_INDEX = -2;
    public static readonly EVENT_TYPE = EventType;
    public cycleSelectionFromHead: boolean = false;
    public cycleSelectionFromTail: boolean = false;

    private _valueOptions: T[];
    private _idx: number = 0;

    constructor(optionSet: IValueSet<T>);
    constructor(options: Readonly<T[]>, defaultValue?: T);
    constructor(options: Readonly<T[]> | IValueSet<T>, defaultValue?: T) {
        super();
        if (Array.isArray(options)) {
            this._valueOptions = [...options];
            if (defaultValue !== undefined && defaultValue !== null) {
                this.value = defaultValue;
            }
        } else {
            const optionSet = options as IValueSet<T>;
            this._valueOptions = [...optionSet.values];
            this.currentIdx = optionSet.defaultIndex ?? 0;
        }
    }

    public set value(value: T) {
        const index: number = this._valueOptions.indexOf(value);
        if (index !== -1) {
            this.currentIdx = index;
        } else {
            error(`Value ${value} is not in the value options.`);
        }
    }
    public get value() {
        return this._valueOptions[this.currentIdx];
    }

    public set currentIdx(newIdx: number) {
        this.setIndex(newIdx);
    }
    public get currentIdx() {
        return this._idx;
    }

    public get options() {
        return this._valueOptions;
    }

    public get isFirstValue() {
        return this.currentIdx === 0;
    }

    public get isLastValue() {
        return this.currentIdx === this._valueOptions.length - 1;
    }

    public get isNullValue() {
        return this.currentIdx === ValueSelector.NULL_INDEX;
    }

    public static calculateClosestSmallNumber(value: number, options: Readonly<number[]>) {
        // Find the closest value that is smaller or equal than the current value
        const closestSmaller = options.filter((bet) => bet <= value).reduce((closest, bet) => (bet > closest ? bet : closest), 0);

        // If no smaller value is found, take the closest value that is larger
        if (closestSmaller === 0) {
            return options.filter((bet) => bet > value).reduce((closest, bet) => (bet < closest ? bet : closest), Infinity);
        }
        return closestSmaller;
    }

    public updateOptions(values: T[]): void {
        if (!values || !values.length) {
            return;
        }
        this._valueOptions = values;
        this._idx = Math.min(this.currentIdx, values.length - 1);
        this._checkIndex();
        this.emit(EventType.OPTIONS_UPDATED, this._valueOptions);
    }

    public setIndex(newIdx: number, notify: boolean = true) {
        if (newIdx === this._idx) {
            return;
        }
        this._idx = newIdx;
        this._checkIndex();
        if (notify) {
            this.emit(EventType.VALUE_CHANGED, this._valueOptions[this._idx]);
        }
    }

    /** 變更為下個數值 */
    public nextValue() {
        if (this.currentIdx === ValueSelector.NULL_INDEX) {
            throw new Error("Can't change to next value when no value is selected.");
        }
        this.currentIdx++;
    }

    /** 變更為上個數值 */
    public previousValue() {
        if (this.currentIdx === ValueSelector.NULL_INDEX) {
            throw new Error("Can't change to previous value when no value is selected.");
        }
        this.currentIdx--;
    }

    /** 變更為第一個數值 */
    public firstValue() {
        this.currentIdx = 0;
    }

    /** 變更為最後一個數值 */
    public lastValue() {
        this.currentIdx = this._valueOptions.length - 1;
    }

    /**
     * 檢查當前 index 是否合法
     */
    private _checkIndex() {
        if (this.isNullValue) {
            return;
        }
        if (this._idx < 0) {
            if (this.cycleSelectionFromHead) {
                this._idx = this._valueOptions.length - 1;
            } else {
                this._idx = 0;
            }
        }
        if (this._idx >= this._valueOptions.length) {
            if (this.cycleSelectionFromTail) {
                this._idx = 0;
            } else {
                this._idx = this._valueOptions.length - 1;
            }
        }
    }
}
